import { Attachment } from "@/api/attachment";
import { SupportedLocale } from "@/i18n";
import { Multipleprint, Multiplesample } from "@/lib/types";
import { useCallback, useMemo, useState } from "react";
import { MultipleprintFullscreenViewer } from "./MultipleprintFullscreenViewer";

function getAttachmentUrl(attachment: Attachment): string | null {
  return (
    attachment.largePublicUrl ??
    attachment.mediumPublicUrl ??
    attachment.publicUrl ??
    null
  );
}

function getAttachmentLegend(attachment: Attachment, locale: string): string {
  const caption = (attachment.caption as Record<string, string> | undefined)?.[locale];
  const textAlt = (attachment.textAlt as Record<string, string> | undefined)?.[locale];
  return caption || textAlt || attachment.name || "";
}

interface MultipleprintImagesProps {
  mainImage: Attachment | null;
  otherImages: Attachment[];
  data: Multipleprint;
  locale: SupportedLocale;
  t: (key: string) => string;
  dimensionsSummaryCalculated: string;
  supportTechniqueCalculated: string;
  itemType?: "MULTIPLE" | "EXEMPLAIRE";
  isExemplaire?: boolean;
  selectedSample?: Multiplesample | null;
}

export function MultipleprintImages({
  mainImage,
  otherImages,
  data,
  locale,
  t,
  dimensionsSummaryCalculated,
  supportTechniqueCalculated,
  itemType: _itemType,
  isExemplaire: _isExemplaire,
  selectedSample: _selectedSample,
}: MultipleprintImagesProps) {
  const allImages = useMemo(
    () => (mainImage ? [mainImage, ...otherImages] : otherImages),
    [mainImage, otherImages],
  );

  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenAttachment, setFullscreenAttachment] =
    useState<Attachment | null>(null);

  const openFullscreen = useCallback((attachment: Attachment) => {
    setFullscreenAttachment(attachment);
    setFullscreenOpen(true);
  }, []);

  if (allImages.length === 0) return null;

  const mainAttachment = allImages[0];
  const thumbnails = otherImages;

  return (
    <>
      <div className="w-full flex flex-col gap-4">
        {/* Main image on top — click opens fullscreen modal with infos */}
        <div
          className="w-full [container-type:inline-size] cursor-pointer"
          onClick={() => openFullscreen(mainAttachment)}
          onKeyDown={(e) =>
            e.key === "Enter" && openFullscreen(mainAttachment)
          }
          role="button"
          tabIndex={0}
        >
          {getAttachmentUrl(mainAttachment) && (
            <img
              src={getAttachmentUrl(mainAttachment)!}
              alt={
                (mainAttachment.textAlt as Record<string, string> | undefined)?.[locale] ??
                (mainAttachment.caption as Record<string, string> | undefined)?.[locale] ??
                data.estampesMultiplesDescriptionTitre ??
                ""
              }
              className="w-full object-contain max-h-[170cqw]"
            />
          )}
        </div>

        {/* Nom de l'illustration principale (ex. « État final »), en légende sous
            l'image — sans libellé « Illustration principale ». */}
        {mainImage?.name && (
          <p className="font-normal text-sm text-muted-foreground px-1 text-left">
            {mainImage.name}
          </p>
        )}

        {/* Thumbnails at bottom with legend — click opens same modal */}
        {thumbnails.length > 0 && (
          <div className="w-full flex flex-wrap gap-4 [container-type:inline-size] mt-2">
            {thumbnails.map((attachment) => {
              const url = getAttachmentUrl(attachment);
              const legend = getAttachmentLegend(attachment, locale);
              return (
                <button
                  key={attachment.id}
                  type="button"
                  onClick={() => openFullscreen(attachment)}
                  className="flex flex-col items-center gap-2 text-left max-w-20 group"
                >
                  {url ? (
                    <div className="w-full max-w-20 max-h-[170cqw] overflow-hidden flex-shrink-0 group-hover:opacity-90 transition-opacity">
                      <img
                        src={url}
                        alt={legend || ""}
                        className="xs:max-w-20 md:w-full max-h-[170cqw] w-full object-contain object-top"
                      />
                    </div>
                  ) : (
                    <div className="max-w-20 w-20 min-h-20 bg-muted flex-shrink-0 rounded" />
                  )}
                  {legend && (
                    <span className="text-xs text-muted-foreground line-clamp-2 text-center w-full px-1">
                      {legend}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {fullscreenAttachment && (
        <MultipleprintFullscreenViewer
          open={fullscreenOpen}
          onClose={() => setFullscreenOpen(false)}
          attachment={fullscreenAttachment}
          data={data}
          locale={locale}
          t={t}
          dimensionsSummaryCalculated={dimensionsSummaryCalculated}
          supportTechniqueCalculated={supportTechniqueCalculated}
        />
      )}
    </>
  );
}
